import {
    ALLOWED_DOCUMENT_MIME_TYPES,
    CreateOrUpdateDocumentParams,
    type DocumentDTO,
    type DocumentOrigin,
    DocumentTagDTO,
    fileExtension,
    type Form,
    MaxFileSize,
    type MimeType,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { html } from "lit-html";
import { customElement, query, state } from "lit/decorators.js";
import { alert } from "../../elements/alert-dialog";
import { DateInput } from "../../elements/date-input";
import { Dialog } from "../../elements/dialog";
import "../../elements/employee-select";
import type { EmployeeSelect } from "../../elements/employee-select";
import "../../elements/popover";
import { app } from "../../init";
import { readFileAsDataURL } from "../../lib/util";
import "./document-tags-input";
import type { DocumentTagsInput } from "./document-tags-input";
import "./file-input";
import type { FileInput } from "./file-input";

//DocumentProps is used as an intermediary type between DocumentDTO and CreateOrUpdateDocumentParams
//TODO: Make conversion more explicit or use params type directly in editor
// `origin` is server-derived on save, so it's optional for not-yet-created documents.
type DocumentProps = Omit<DocumentDTO, "type" | "origin"> & { type?: MimeType; origin?: DocumentOrigin };

@customElement("ptc-edit-document-dialog")
export class EditDocumentDialog extends Dialog<
    { document: DocumentProps; file?: File; form?: Form } | undefined,
    DocumentDTO
> {
    readonly preventDismiss = true;

    @query("form")
    private _form: HTMLFormElement;

    @query("ptc-file-input")
    private _fileInput: FileInput;

    @query("[name='name']")
    private _nameInput: HTMLInputElement;

    @query("textarea[name='comment']")
    private _commentInput: HTMLTextAreaElement;

    @query("ptc-employee-select")
    private _employeeSelect: EmployeeSelect;

    @query("ptc-date-input")
    private _dateInput: DateInput;

    @query("ptc-document-tags-input")
    private _tagsInput: DocumentTagsInput;

    @state()
    private _newFile?: File;

    @state()
    private _document: DocumentProps;

    @state()
    private _availableTags: DocumentTagDTO[] = [];

    async show({ document, file }: { document: DocumentProps; file?: File }) {
        const promise = super.show();
        this._newFile = undefined;

        this._document = document;

        // Load available document tags
        try {
            this._availableTags = await app.api.getDocumentTagsForCompany();
        } catch (error) {
            console.error("Failed to load document tags:", error);
            this._availableTags = [];
        }

        await this.updateComplete;

        if (file) {
            await this._fileSelected(file);
        }

        this._nameInput.value = this._document.name;
        this._employeeSelect.employeeId = this._document.employeeId || null;
        this._dateInput.value = this._document.date;
        this._commentInput.value = this._document.comment || "";

        if (this._newFile) {
            this._fileInput.file = this._newFile;
        } else if (this._document.url && this._document.type) {
            this._fileInput.file = new File([], `${this._document.name}.${fileExtension(this._document.type)}`);
        } else if (this._fileInput) {
            this._fileInput.file = null;
        }

        // Convert Document tags to DTOs for the input component
        this._tagsInput.tags =
            this._document.tags?.map(
                (tag) =>
                    new DocumentTagDTO({
                        id: tag.id,
                        name: tag.name,
                        color: tag.color,
                        order: tag.order,
                        hideFromEmployees: tag.hideFromEmployees,
                    })
            ) || [];
        return promise;
    }

    private async _fileSelected(file?: File | null) {
        file = file || this._fileInput.file;

        if (file && file.size > MaxFileSize) {
            void alert("Dateien dürfen nicht größer als 10MB sein!", { title: "Datei zu groß", type: "warning" });
            this._fileInput.file = null;
            return;
        }

        if (file) {
            this._nameInput.value = file.name.replace(new RegExp(`.${file.name.split(".").pop()}$`), "");
            if (file.lastModified) {
                this._dateInput.value = toDateString(new Date(file.lastModified));
            }
        }
        this._newFile = file || undefined;
    }

    private async _submit(e: Event) {
        e.preventDefault();

        const data = new FormData(this._form);

        const file = this._fileInput?.file;

        const content = file && this._newFile && (await readFileAsDataURL(file));
        const name = data.get("name") as string;
        const date = data.get("date") as DateString;
        const comment = data.get("comment") as string;
        const tags = this._tagsInput.tags;
        const employeeId = this._employeeSelect.employeeId;

        if (!employeeId) {
            return;
        }
        const employee = app.getEmployee(employeeId)!;

        if (this._document.form) {
            this._document.form.prefillForEmployee(employee);
        }

        this.loading = true;

        try {
            const document = await app.api.createOrUpdateDocument(
                new CreateOrUpdateDocumentParams({
                    id: this._document?.id,
                    employeeId,
                    name,
                    date,
                    comment,
                    content,
                    tags,
                    form: this._document.form,
                })
            );
            this.done(document);
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this.loading = false;
    }

    static styles = [...Dialog.styles, DateInput.styles];

    renderContent() {
        return html`
            <form class="double-padded" @submit=${this._submit}>
                <div class="faded bottom-margined">
                    ${this._document.id
                        ? html`<ptc-ui-icon name="icon.action.edit-document"></ptc-ui-icon> Dokument Bearbeiten`
                        : html`<ptc-ui-icon name="icon.action.upload"></ptc-ui-icon> Neues Dokument`}
                </div>

                <div class="field">
                    <label>Name</label>
                    <input name="name" required />
                </div>

                <div class="field">
                    <label>Mitarbeiter</label>
                    <ptc-employee-select required></ptc-employee-select>
                </div>

                <div class="horizontal spacing layout">
                    <div class="field">
                        <label>Datum</label>
                        <ptc-date-input name="date" required></ptc-date-input>
                    </div>

                    ${this._document.form
                        ? html`
                              <div class="field stretch collapse">
                                  <label>Formular</label>
                                  <div class="padded box spacing center-aligning horizontal layout">
                                      <div class="stretch ellipsis collapse">
                                          <ptc-ui-icon name="icon.object.form"></ptc-ui-icon>
                                          ${this._document.form.name}
                                      </div>
                                      <div class="tiny pill">Entwurf</div>
                                  </div>
                              </div>
                          `
                        : html`
                              <div class="field stretch collapse">
                                  <label>Datei</label>
                                  <ptc-file-input
                                      @change=${() => this._fileSelected()}
                                      ?required=${!this._document.id && !this._newFile}
                                      .acceptTypes=${ALLOWED_DOCUMENT_MIME_TYPES}
                                  ></ptc-file-input>
                              </div>
                          `}
                </div>

                <div class="field">
                    <label>Kategorien</label>
                    <ptc-document-tags-input
                        .availableTags=${this._availableTags}
                        @change=${() => this.requestUpdate()}
                    >
                    </ptc-document-tags-input>
                    ${this._tagsInput?.tags.some((tag) => tag.hideFromEmployees)
                        ? html`
                              <div class="small padded top-margined orange box">
                                  <ptc-ui-icon name="icon.toggle.visibility-hidden"></ptc-ui-icon> Dieses Dokument kann
                                  aufgrund einer oder mehrerer der gewählten Kategorien nicht vom Mitarbeiter eingesehen
                                  werden.
                              </div>
                          `
                        : ""}
                </div>

                <div class="field">
                    <label>Kommentar</label>
                    <textarea name="comment"></textarea>
                </div>

                <div class="top-padded horizontal stretching spacing layout">
                    <button class="primary">Speichern</button>
                    <button class="transparent" type="button" @click=${() => this.done()}>Abbrechen</button>
                </div>
            </form>
        `;
    }
}
